/**
 * components/motion.ts — the phone's «уменьшение движения», read and kept current.
 *
 * Task 09 kept this inside `Vinyl.tsx`; task 18 needs it on «Сейчас играет» as well — a phone
 * that asked not to be moved gets no gesture, so no track is downloaded a second time for a
 * scratch nobody can make. One subscription per component, not one per render: React is given
 * the same `subscribe` function every time.
 */

import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

import { TURN_S } from '../lib/records';
import type { RecordSize } from '../lib/records';
import { tempoRate } from '../store/deck';

export const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function reducedMotionQuery(): MediaQueryList | undefined {
  const page: Partial<Pick<Window, 'matchMedia'>> = window;
  return page.matchMedia?.(REDUCED_MOTION);
}

function subscribe(onChange: () => void): () => void {
  const query = reducedMotionQuery();
  query?.addEventListener('change', onChange);
  return () => {
    query?.removeEventListener('change', onChange);
  };
}

function read(): boolean {
  return reducedMotionQuery()?.matches ?? false;
}

/** `true` while the phone asks for reduced motion. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, read);
}

export type Animatable = Partial<Pick<Element, 'getAnimations'>>;

/**
 * Task 23: the turns at the tempo's speed — the record, and the platter's motor under it, 8 %
 * faster at +8 %, as the sound is. Web Animations only: without them the turn stays at 1,0.
 */
export function turnAt(nodes: readonly (Animatable | null)[], rate: number): void {
  for (const node of nodes) {
    for (const animation of node?.getAnimations?.() ?? []) {
      // Only a change is written: a turn spinning up after a finger is not to be restarted.
      if (animation.playbackRate !== rate) animation.playbackRate = rate;
    }
  }
}

/**
 * Task 21: a turn that changes its period without a jump. A CSS animation keeps its clock when
 * its duration changes, so the same clock over a longer turn is a different angle — the record
 * would leap as 45 becomes 33. The clock is moved instead, to where the new turn has the angle
 * the old one had. Web Animations only: without them, a leap is all that is lost.
 */
export function keepAngle(nodes: readonly (Animatable | null)[], fromS: number, toS: number): void {
  if (fromS === toS) return;
  for (const node of nodes) {
    for (const animation of node?.getAnimations?.() ?? []) {
      const clock = animation.currentTime;
      if (typeof clock !== 'number') continue;
      const fromMs = fromS * 1000;
      animation.currentTime = ((clock % fromMs) / fromMs) * toS * 1000;
    }
  }
}

/**
 * Tasks 21 and 23, for everything on the deck that turns — the record, the platter's two layers:
 * a new record turns on from the angle it had ({@link keepAngle}), and every turn goes at the
 * tempo's pace ({@link turnAt}). `nodes` must be the same array from render to render
 * (`useMemo`): the effects answer the record and the tempo, not a render.
 */
export function useDeckTurn(
  nodes: readonly RefObject<Animatable | null>[],
  size: RecordSize,
  pitch: number,
): void {
  const shownSize = useRef(size);
  useLayoutEffect(() => {
    keepAngle(
      nodes.map((node) => node.current),
      TURN_S[shownSize.current],
      TURN_S[size],
    );
    shownSize.current = size;
  }, [nodes, size]);
  useEffect(() => {
    turnAt(
      nodes.map((node) => node.current),
      tempoRate(pitch),
    );
  }, [nodes, pitch]);
}
